import { NotificationData } from '@/types';

class NotificationService {
  private notifications: NotificationData[] = [];
  private notificationListeners: ((notifications: NotificationData[]) => void)[] = [];
  private nextId = 1;

  // Aggiungi listener per aggiornamenti notifiche
  addNotificationListener(listener: (notifications: NotificationData[]) => void): void {
    this.notificationListeners.push(listener);
  }

  // Rimuovi listener
  removeNotificationListener(listener: (notifications: NotificationData[]) => void): void {
    this.notificationListeners = this.notificationListeners.filter(l => l !== listener);
  }

  // Notifica aggiornamenti a tutti i listener
  private notifyListeners(): void {
    this.notificationListeners.forEach(listener => listener([...this.notifications]));
  }

  // Aggiungi notifica
  addNotification(
    type: NotificationData['type'],
    title: string,
    message: string,
    duration: number = 5000
  ): string {
    const notification: NotificationData = {
      id: `notification_${this.nextId++}`,
      type,
      title,
      message,
      timestamp: Date.now(),
      duration
    };

    this.notifications.push(notification);
    this.notifyListeners();

    // Auto-rimozione se specificata durata
    if (duration > 0) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, duration);
    }

    return notification.id;
  }

  // Rimuovi notifica
  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  // Rimuovi tutte le notifiche
  clearAllNotifications(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  // Ottieni tutte le notifiche
  getNotifications(): NotificationData[] {
    return [...this.notifications];
  }

  // Metodi di convenienza per diversi tipi di notifiche
  success(title: string, message: string, duration?: number): string {
    return this.addNotification('success', title, message, duration);
  }

  error(title: string, message: string, duration?: number): string {
    return this.addNotification('error', title, message, duration);
  }

  warning(title: string, message: string, duration?: number): string {
    return this.addNotification('warning', title, message, duration);
  }

  info(title: string, message: string, duration?: number): string {
    return this.addNotification('info', title, message, duration);
  }

  // Notifiche specifiche per l'app
  donationCreated(amount: number): string {
    return this.success(
      'Donazione creata',
      `Donazione di €${amount.toFixed(2)} salvata con successo`,
      3000
    );
  }

  donationSynced(amount: number): string {
    return this.success(
      'Donazione sincronizzata',
      `Donazione di €${amount.toFixed(2)} sincronizzata con il server`,
      3000
    );
  }

  syncStarted(): string {
    return this.info(
      'Sincronizzazione avviata',
      'Sto sincronizzando i dati offline...',
      0 // Non auto-rimuovere
    );
  }

  syncCompleted(syncedDonations: number, syncedReceipts: number): string {
    return this.success(
      'Sincronizzazione completata',
      `${syncedDonations} donazioni e ${syncedReceipts} ricevute sincronizzate`,
      5000
    );
  }

  syncFailed(errors: string[]): string {
    return this.error(
      'Sincronizzazione fallita',
      `Errori: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`,
      8000
    );
  }

  offlineMode(): string {
    return this.warning(
      'Modalità offline',
      'L\'app funziona in modalità offline. I dati verranno sincronizzati quando tornerai online.',
      5000
    );
  }

  onlineMode(): string {
    return this.success(
      'Connessione ripristinata',
      'Sincronizzazione automatica in corso...',
      3000
    );
  }

  locationPermissionDenied(): string {
    return this.warning(
      'Geolocalizzazione negata',
      'Non è possibile ottenere la posizione. La donazione verrà salvata senza coordinate.',
      5000
    );
  }

  locationUnavailable(): string {
    return this.warning(
      'Posizione non disponibile',
      'Impossibile ottenere la posizione corrente. La donazione verrà salvata senza coordinate.',
      5000
    );
  }

  receiptUploaded(): string {
    return this.success(
      'Ricevuta caricata',
      'La foto della ricevuta è stata caricata con successo',
      3000
    );
  }

  receiptUploadFailed(): string {
    return this.error(
      'Caricamento ricevuta fallito',
      'Impossibile caricare la foto della ricevuta. Riprova più tardi.',
      5000
    );
  }

  donorCreated(name: string): string {
    return this.success(
      'Donatore creato',
      `Donatore ${name} aggiunto con successo`,
      3000
    );
  }

  donorUpdated(name: string): string {
    return this.success(
      'Donatore aggiornato',
      `Dati del donatore ${name} aggiornati`,
      3000
    );
  }

  // Notifiche di sistema
  appInstalled(): string {
    return this.success(
      'App installata',
      'L\'app è stata installata con successo sul tuo dispositivo',
      5000
    );
  }

  updateAvailable(): string {
    return this.info(
      'Aggiornamento disponibile',
      'È disponibile una nuova versione dell\'app. Ricarica la pagina per aggiornare.',
      0 // Non auto-rimuovere
    );
  }

  // Gestione notifiche persistenti
  getPersistentNotifications(): NotificationData[] {
    return this.notifications.filter(n => n.duration === 0);
  }

  // Rimuovi notifiche persistenti
  clearPersistentNotifications(): void {
    this.notifications = this.notifications.filter(n => (n.duration || 0) > 0);
    this.notifyListeners();
  }

  // Reset completo del servizio (per logout)
  reset(): void {
    this.notifications = [];
    this.notificationListeners = [];
    this.nextId = 1;
    console.log('Notification service reset completato');
  }
}

export const notificationService = new NotificationService();
export default notificationService;
